import { cn } from '@/shared/utilities';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { User } from '../../models/user';
import { useInfiniteUserSearch, useInvalidateUserSearch } from '../../queries/use-infinite-user-search';
import { UserItem } from './user-item';

export interface UserListProps {
  /** Placeholder cho ô tìm kiếm */
  searchTerm?: Nullable<string>;
  /** Chiều cao tối đa của danh sách */
  maxHeight?: number | string;
  /** CSS class tùy chỉnh */
  className?: string;
  /** Hiển thị chức vụ và đơn vị */
  showPosition?: boolean;
  /** Disable component */
  disabled?: boolean;
  /** Page size cho infinite loading */
  pageSize?: number;
  /** Danh sách người dùng đã chọn */
  selectedUsers?: User[];
  /** Callback khi danh sách người dùng được chọn (multiple select) */
  onUsersSelect?: (users: User[]) => void;
  /** Callback khi người dùng được chọn (single select) */
  onUserSelect?: (user: User) => void;
}

export interface UserListRef {
  scrollToUserId: (userId: string) => boolean;
  scrollToTop: () => void;
  refresh: () => void;
}

const UserList = forwardRef<UserListRef, UserListProps>(function UserList(
  {
    searchTerm,
    className,
    selectedUsers,
    onUsersSelect,
    onUserSelect,
    showPosition = true,
    disabled = false,
    pageSize = 20
  },
  ref
) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const invalidateUserSearch = useInvalidateUserSearch();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm || '');
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { allUsers, loadMoreUsers, isLoadingMore, canLoadMore, error, refetch } = useInfiniteUserSearch({
    searchTerm: debouncedSearchTerm,
    enabled: !disabled,
    pageSize
  });

  const memoizedUsers = useMemo(() => allUsers, [allUsers.length]);

  const selectedIds = useMemo(() => new Set(selectedUsers?.map((u) => u.id) || []), [selectedUsers]);

  const virtualizer = useVirtualizer({
    count: memoizedUsers.length,
    estimateSize: () => 50, // Estimated height per user item
    getScrollElement: () => parentRef.current,
    overscan: 5,
    getItemKey: (index) => memoizedUsers[index]?.id || index
  });

  const handleUserClick = useCallback(
    (user: User) => {
      if (disabled) return;

      if (onUserSelect) {
        // Single select mode
        onUserSelect(user);
      } else if (onUsersSelect) {
        // Multiple select mode
        const isSelected = selectedIds.has(user.id);
        const newSelectedUsers = isSelected
          ? (selectedUsers || []).filter((u) => u.id !== user.id)
          : [...(selectedUsers || []), user];
        onUsersSelect(newSelectedUsers);
      }
    },
    [disabled, onUserSelect, onUsersSelect, selectedIds, selectedUsers]
  );

  // Load more when reaching end
  const handleEndReached = useCallback(() => {
    if (canLoadMore && !isLoadingMore) {
      loadMoreUsers();
    }
  }, [canLoadMore, isLoadingMore, loadMoreUsers]);

  // Check if at bottom for load more
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        // Threshold
        handleEndReached();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [handleEndReached]);

  // Scroll to specific user by ID
  const scrollToUserId = useCallback(
    (userId: string): boolean => {
      if (!userId) return false;
      const userIndex = memoizedUsers.findIndex((user) => user.id === userId);

      if (userIndex >= 0) {
        virtualizer.scrollToIndex(userIndex, { align: 'center' });
        return true;
      }
      return false;
    },
    [memoizedUsers, virtualizer]
  );

  // Scroll to top
  const scrollToTop = useCallback(() => {
    if (memoizedUsers.length > 0) {
      virtualizer.scrollToIndex(0, { align: 'start' });
    }
  }, [memoizedUsers, virtualizer]);

  // Refresh data
  const refresh = useCallback(() => {
    invalidateUserSearch(debouncedSearchTerm);
    refetch();
  }, [invalidateUserSearch, debouncedSearchTerm, refetch]);

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      scrollToUserId,
      scrollToTop,
      refresh
    }),
    [scrollToUserId, scrollToTop, refresh]
  );

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const viewportH = parentRef.current?.clientHeight ?? 0;

  // Error state
  if (error) {
    return (
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        height='100%'
        gap={2}
        className={cn('mex:flex mex:flex-col', className)}
      >
        <Alert
          color='error'
          className='mex:items-center'
          icon={
            <IconButton onClick={refresh} color='error'>
              <RefreshIcon />
            </IconButton>
          }
        >
          Tải danh sách người dùng thất bại
        </Alert>
      </Box>
    );
  }

  return (
    <Box ref={parentRef} className={cn('mex:flex mex:h-full mex:overflow-y-auto mex:overflow-x-hidden', className)}>
      <Box
        component='div'
        className='mex:flex mex:divide-y mex:divide-gray-200'
        sx={{
          height: Math.max(totalSize, viewportH),
          position: 'relative',
          width: '100%'
        }}
      >
        {items.map((item) => {
          const user = memoizedUsers[item.index];
          return (
            <Box
              key={item.key}
              ref={virtualizer.measureElement}
              sx={{
                top: 0,
                position: 'absolute',
                width: '100%',
                transform: `translateY(${item.start}px)`
              }}
              data-index={item.index}
              tabIndex={-1}
            >
              {user && (
                <UserItem
                  user={user}
                  isSelected={onUsersSelect ? selectedIds.has(user.id) : false}
                  showPosition={showPosition}
                  onClick={handleUserClick}
                  disabled={disabled}
                  className=''
                />
              )}
            </Box>
          );
        })}
        {isLoadingMore && (
          <Box display='flex' justifyContent='center' py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  );
});

export { UserList };
